import { useCallback, useState } from 'react';
import type { LoadoutProps } from '@sorrem-client/ui/components';
import type { CharacterEquipmentLoadout, Skill } from '@sorrem/shared';
import { AbilityPicker } from './AbilityPicker';

export interface AbilityPickerMenu {
    /** Spread onto the `Loadout`: right-clicking an action frame opens the picker above it. */
    readonly loadoutProps: Required<Pick<LoadoutProps, 'actionMenuIndex' | 'onActionMenuChange' | 'actionMenu'>>;
    /** Whether the picker is open, e.g. so Esc closes it before anything else. */
    readonly isOpen: boolean;
    readonly close: () => void;
}

/**
 * The loadout's right-click ability picker (#241): which action frame it is
 * open on, and what it shows. The picker closes itself on a pick, on Esc, or
 * on a click outside it.
 */
export function useAbilityPickerMenu(
    /** The character's skills and gear, so each ability shows what it costs them (as the slot summary does). */
    skills?: readonly Skill[],
    equipped?: CharacterEquipmentLoadout,
): AbilityPickerMenu {
    const [menuIndex, setMenuIndex] = useState<number | null>(null);
    const close = useCallback((): void => setMenuIndex(null), []);

    return {
        loadoutProps: {
            actionMenuIndex: menuIndex,
            onActionMenuChange: setMenuIndex,
            actionMenu: menuIndex === null ? null : <AbilityPicker hotkeyIndex={menuIndex} onDone={close} skills={skills} equipped={equipped} />,
        },
        isOpen: menuIndex !== null,
        close,
    };
}
